(() => {
  "use strict";

  const SELECTOR = ".qp-inline-product";
  const SOURCE_LINK_SELECTOR =
    '.qp-inline-product__source, a[href*="/products/"]';

  document.documentElement.classList.add("qp-product-js");

  function getSourceLink(container) {
    return container.querySelector(SOURCE_LINK_SELECTOR);
  }

  function getProductUrl(container) {
    const sourceLink = getSourceLink(container);

    return (
      container.dataset.productUrl?.trim() ||
      sourceLink?.getAttribute("href")?.trim() ||
      ""
    );
  }

  function getButtonText(container) {
    const sourceLink = getSourceLink(container);

    return (
      container.dataset.buttonText?.trim() ||
      sourceLink?.textContent?.trim() ||
      "View product"
    );
  }

  function getHandle(productUrl) {
    if (!productUrl) {
      return "";
    }

    try {
      const url = new URL(
        productUrl,
        window.location.origin
      );

      const match = url.pathname.match(
        /\/products\/([^/?#]+)/
      );

      if (!match || !match[1]) {
        return "";
      }

      let handle = match[1];

      try {
        handle = decodeURIComponent(handle);
      } catch (decodeError) {
        console.warn(
          "Product handle was already decoded:",
          handle
        );
      }

      return handle.trim();
    } catch (error) {
      console.error(
        "Invalid product URL:",
        productUrl,
        error
      );

      return "";
    }
  }

  function formatMoney(amount) {
    const currency =
      window.Shopify?.currency?.active || "HKD";

    return new Intl.NumberFormat(
      document.documentElement.lang || "en-HK",
      {
        style: "currency",
        currency
      }
    ).format(Number(amount || 0) / 100);
  }

  function plainText(html) {
    const element = document.createElement("div");

    element.innerHTML = html || "";

    return (element.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function truncate(text, limit = 130) {
    if (!text || text.length <= limit) {
      return text || "";
    }

    return `${text.slice(0, limit).trim()}…`;
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);

    if (className) {
      element.className = className;
    }

    if (text !== undefined && text !== null) {
      element.textContent = text;
    }

    return element;
  }

  function getStatusText(container, available) {
    const language = (
      document.documentElement.lang || ""
    ).toLowerCase();

    const isChinese =
      language.startsWith("zh") ||
      getButtonText(container).includes("查看");

    if (isChinese) {
      return available ? "有現貨" : "售罄";
    }

    return available ? "In stock" : "Sold out";
  }

  function getFromText(container) {
    const language = (
      document.documentElement.lang || ""
    ).toLowerCase();

    const isChinese =
      language.startsWith("zh") ||
      getButtonText(container).includes("查看");

    return isChinese ? "售價由" : "From";
  }

  function getErrorText(container) {
    const language = (
      document.documentElement.lang || ""
    ).toLowerCase();

    const isChinese =
      language.startsWith("zh") ||
      getButtonText(container).includes("查看");

    return isChinese
      ? "暫時未能載入產品資料。"
      : "Product information is currently unavailable.";
  }

  function renderProductCard(
    container,
    product,
    productUrl
  ) {
    const buttonText = getButtonText(container);

    const card = createElement(
      "article",
      "qp-inline-product__card"
    );

    const imageLink = createElement(
      "a",
      "qp-inline-product__image-link"
    );

    imageLink.href = productUrl;
    imageLink.setAttribute(
      "aria-label",
      `${buttonText}: ${product.title}`
    );

    if (product.featured_image) {
      const image = document.createElement("img");

      image.className = "qp-inline-product__image";
      image.src = product.featured_image;
      image.alt = product.title || "";
      image.loading = "lazy";
      image.decoding = "async";

      imageLink.appendChild(image);
    }

    const content = createElement(
      "div",
      "qp-inline-product__content"
    );

    if (product.vendor) {
      content.appendChild(
        createElement(
          "p",
          "qp-inline-product__vendor",
          product.vendor
        )
      );
    }

    const title = createElement(
      "h3",
      "qp-inline-product__title"
    );

    const titleLink = createElement("a");

    titleLink.href = productUrl;
    titleLink.textContent = product.title;

    title.appendChild(titleLink);
    content.appendChild(title);

    const description = truncate(
      plainText(product.description)
    );

    if (description) {
      content.appendChild(
        createElement(
          "p",
          "qp-inline-product__description",
          description
        )
      );
    }

    const priceRow = createElement(
      "div",
      "qp-inline-product__price-row"
    );

    if (product.price_varies) {
      priceRow.appendChild(
        createElement(
          "span",
          "qp-inline-product__from",
          getFromText(container)
        )
      );
    }

    priceRow.appendChild(
      createElement(
        "span",
        "qp-inline-product__price",
        formatMoney(product.price)
      )
    );

    if (
      product.compare_at_price &&
      product.compare_at_price > product.price
    ) {
      priceRow.appendChild(
        createElement(
          "s",
          "qp-inline-product__compare-price",
          formatMoney(product.compare_at_price)
        )
      );
    }

    content.appendChild(priceRow);

    content.appendChild(
      createElement(
        "p",
        product.available
          ? "qp-inline-product__status qp-inline-product__status--available"
          : "qp-inline-product__status qp-inline-product__status--sold-out",
        getStatusText(container, product.available)
      )
    );

    const button = createElement(
      "a",
      "qp-inline-product__button",
      buttonText
    );

    button.href = productUrl;
    button.setAttribute(
      "aria-label",
      `${buttonText}: ${product.title}`
    );

    content.appendChild(button);

    card.appendChild(imageLink);
    card.appendChild(content);

    container.replaceChildren(card);
    container.classList.remove(
      "qp-inline-product--error"
    );
    container.classList.add(
      "qp-inline-product--loaded"
    );
  }

  function showError(container) {
    const sourceLink = getSourceLink(container);

    const fallbackLink = sourceLink
      ? sourceLink.cloneNode(true)
      : null;

    const error = createElement(
      "p",
      "qp-inline-product__error",
      getErrorText(container)
    );

    container.replaceChildren(error);
    container.classList.add(
      "qp-inline-product--error"
    );

    if (fallbackLink) {
      fallbackLink.classList.add(
        "qp-inline-product__fallback"
      );
      fallbackLink.removeAttribute("hidden");
      fallbackLink.style.display = "inline-flex";
      container.appendChild(fallbackLink);
    }
  }

  async function loadProduct(container) {
    if (
      container.dataset.productLoaded === "true"
    ) {
      return;
    }

    const productUrl = getProductUrl(container);
    const handle = getHandle(productUrl);

    if (!productUrl || !handle) {
      console.warn(
        "No valid product URL found:",
        container
      );
      showError(container);
      return;
    }

    container.dataset.productLoaded = "true";

    const root =
      window.Shopify?.routes?.root || "/";

    const normalisedRoot = root.endsWith("/")
      ? root
      : `${root}/`;

    const requestUrl =
      `${normalisedRoot}products/${encodeURIComponent(handle)}.js`;

    try {
      const response = await fetch(requestUrl, {
        headers: {
          Accept: "application/json"
        },
        credentials: "same-origin"
      });

      if (!response.ok) {
        throw new Error(
          `Product request failed: ${response.status}`
        );
      }

      const product = await response.json();

      renderProductCard(
        container,
        product,
        productUrl
      );
    } catch (error) {
      console.error(
        "Q-PETS product-card error:",
        error
      );

      showError(container);
    }
  }

  function initialiseProductCards(root = document) {
    if (!root?.querySelectorAll) {
      return;
    }

    root
      .querySelectorAll(SELECTOR)
      .forEach(loadProduct);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initialiseProductCards(),
      { once: true }
    );
  } else {
    initialiseProductCards();
  }

  document.addEventListener(
    "shopify:section:load",
    event => {
      initialiseProductCards(event.target);
    }
  );
})();
